import { useEffect, useRef, useState, type FormEvent } from 'react';
import { WS, type ConnState } from './ws';
import { createRenderer, type Renderer } from './render';
import { concertAudio } from './concertAudio';
import { ambient } from './ambientAudio';
import type { SrvMsg, WirePet, WirePetDyn, WirePetMeta, WireEvent, TimeInfo, LotInfo, Storyline, Leaderboard, ForecastNow, ForecastHour, CityProject } from './protocol';

// (#bw) merge a per-tick dynamic pet with its (cached) constant meta into the full WirePet the renderer needs
const DEFAULT_META = { name: '', app: [0, 0, 0] as [number, number, number], vet: 0, since: 0, rank: 0, rankTier: 0, favSong: '', flair: '', legend: false, named: false };
// (#duskdawn) pure-client PREVIEW overrides: ?tod=18.4 forces a time-of-day (hour 0–24, sun/phase
// derived) and ?wx=rain|snow|storm|fog|… forces the weather — for eyeballing dusk/dawn staging and
// ground-weather without waiting for the real clock. Display-only; the sim is untouched.
const PREVIEW = (() => {
    const q = new URLSearchParams(window.location.search); const tv = q.get('tod');
    return { tod: tv == null ? null : Math.max(0, Math.min(24, parseFloat(tv) || 0)), wx: q.get('wx') || '' };
})();
function mergePet(d: WirePetDyn, meta: WirePetMeta | undefined): WirePet { return { ...(meta ?? DEFAULT_META), ...d } as WirePet; }

interface Focus { pet: WirePet; genome: any; address: string; }

export default function App() {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const wsRef = useRef<WS | null>(null);
    const sceneRef = useRef<Renderer | null>(null);
    const [backend, setBackend] = useState('canvas2d');
    const [conn, setConn] = useState<ConnState>('connecting');
    const [me, setMe] = useState<{ id: number; name: string } | null>(null);
    const [hasPet, setHasPet] = useState(false);
    const [myPetId, setMyPetId] = useState(0);
    const [time, setTime] = useState<TimeInfo>({ hour: 12, phase: 'day', sun: 1, weather: 'clear', season: 'spring', tempF: 60, windMph: 5, weatherDesc: 'clear', festival: null });
    const [focus, setFocus] = useState<Focus | null>(null);
    const [feed, setFeed] = useState<WireEvent[]>([]);
    const [diary, setDiary] = useState<string[] | null>(null);
    const [diaryLoading, setDiaryLoading] = useState(false);
    const [diaryDay, setDiaryDay] = useState('');
    const [forecast, setForecast] = useState<{ now: ForecastNow; hours: ForecastHour[] } | null>(null);
    const [wxOpen, setWxOpen] = useState(false);
    const [festOpen, setFestOpen] = useState(false);
    const [lot, setLot] = useState<LotInfo | null>(null);
    const [mailUnread, setMailUnread] = useState(0);
    const [letters, setLetters] = useState<{ ts: number; subject: string; lines: string[] }[] | null>(null);
    const [cliques, setCliques] = useState<{ id: number; name: string }[]>([]);
    const [storylines, setStorylines] = useState<Storyline[]>([]);
    const [talesOpen, setTalesOpen] = useState(false);
    const [boards, setBoards] = useState<Leaderboard[]>([]);
    const [boardsOpen, setBoardsOpen] = useState(false);
    const [ticker, setTicker] = useState<string[]>([]);
    const [notifs, setNotifs] = useState<WireEvent[]>([]);
    const [notifOpen, setNotifOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    const [popNote, setPopNote] = useState<string | null>(null);
    const myPetIdRef = useRef(0);
    const myNameRef = useRef('');
    const [search, setSearch] = useState('');
    const [toast, setToast] = useState<string | null>(null);
    // (#embed) compact widget for profile pages: ?embed=1 & profile=<name> (or pet=<id>) & view=pet|home
    const embedCfg = useRef((() => {
        const q = new URLSearchParams(location.search);
        return { embed: q.has('embed'), profile: q.get('profile') || '', pet: Number(q.get('pet') || 0), view: (q.get('view') === 'home' ? 'home' : 'pet') as 'pet' | 'home', edit: q.get('edit') === '1' };
    })()).current;
    const embed = embedCfg.embed;
    const [embedView, setEmbedView] = useState<'pet' | 'home'>(embedCfg.view);
    const [embedPetId, setEmbedPetId] = useState(embedCfg.pet);
    const [embedPetName, setEmbedPetName] = useState(embedCfg.profile);
    const embedViewRef = useRef(embedCfg.view);
    const embedPetIdRef = useRef(embedCfg.pet);
    const embedGpsSent = useRef(false);   // (#embed) tell the gateway to keep the embedded pet in-frame (works on any profile)
    const petMetaRef = useRef(new Map<number, WirePetMeta>());   // (#bw) constant per-pet meta, cached across frames
    const [sceneReady, setSceneReady] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [isFull, setIsFull] = useState(false);
    const [camLocked, setCamLocked] = useState(false);   // (#camlock) full-app: keep the camera on my pet
    // (#sound) the ONE audio toggle — muted by default, remembered across visits
    const [audioOn, setAudioOn] = useState(() => { try { return localStorage.getItem('ppets_audio') === '1'; } catch { return false; } });
    const [embedLocked, setEmbedLocked] = useState(true);   // (#embed) widget: camera pinned to the pet (toggleable)
    // (#name) one-time pet naming + privacy
    const [myPetNamed, setMyPetNamed] = useState(false);
    const [nameOpen, setNameOpen] = useState(false);
    // (#profilepets) the adopt infographic (dismissable — they can just watch) + post-name step
    const [adoptDismissed, setAdoptDismissed] = useState(() => { try { return localStorage.getItem('ppet_adopt_dismissed') === '1'; } catch { return false; } });
    const [namedDone, setNamedDone] = useState<string | null>(null);   // pet name → "show on your profile?" step
    const [cityProject, setCityProject] = useState<CityProject | null>(null);   // (#civic) the live funded project
    const [nameInput, setNameInput] = useState('');
    const [nameConfirm, setNameConfirm] = useState('');
    const [nameError, setNameError] = useState('');
    const [nameBusy, setNameBusy] = useState(false);
    const autoPrompted = useRef(false);

    useEffect(() => {
        let ws: WS | null = null;
        let cancelled = false;
        void (async () => {
            const scene = await createRenderer(canvasRef.current!);
            if (cancelled) return;
            sceneRef.current = scene;
            setBackend(scene.backend);
            scene.setEmbed(embed);   // (#embed) compact widget → pin the camera, hide chrome
            // (#sound) returning visitor who left sound ON: arm the engine now (it stays silent
            // until the browser's first-gesture unlock resumes the suspended AudioContext)
            if (audioOn) { ambient.setEnabled(true); scene.setAudioEnabled(true); }
            setSceneReady(true);     // (#embed) lets the camera-apply effect run now the renderer exists
            ws = new WS();
            wsRef.current = ws;
            ws.onstate = (s) => { setConn(s); if (s !== 'open') { embedGpsSent.current = false; sceneRef.current?.resendViewport(); } };   // re-assert the embed's followed pet + viewport after a reconnect
            scene.onPick = (petId) => { ws!.send({ t: 'gps', petId }); scene.setFocus(petId); };
            scene.onInspect = (lotId) => { ws!.send({ t: 'inspect', lotId }); };
            scene.onViewport = (b) => { ws!.send({ t: 'cam', ...b }); };   // (#bw3) the gateway streams only what's on screen
            ws.onmsg = (m: SrvMsg) => {
                if (m.t === 'session') {
                    setMe(m.me); setHasPet(m.hasPet); setMyPetId(m.petId);
                    myPetIdRef.current = m.hasPet ? m.petId : 0; myNameRef.current = m.hasPet ? m.me.name : '';
                    if (m.hasPet) { setTimeout(() => { ws!.send({ t: 'gps', petId: m.petId }); scene.setFocus(m.petId); }, 600); }
                } else if (m.t === 'world') {
                    if (PREVIEW.tod != null) {   // (#duskdawn) preview: derive sun/phase exactly like the server's sunValue()
                        const td = PREVIEW.tod; m.time.hour = td;
                        m.time.sun = Math.max(0, (Math.cos(((td - 13) / 24) * Math.PI * 2) + 1) / 2);
                        m.time.phase = td >= 5 && td < 7 ? 'dawn' : td >= 7 && td < 18 ? 'day' : td >= 18 && td < 20 ? 'dusk' : 'night';
                    }
                    if (PREVIEW.wx) m.time.weather = PREVIEW.wx;
                    // (#bw) merge each dynamic pet with its cached constant meta (sent once); store any new meta first.
                    if (m.petMeta) for (const pm of m.petMeta) petMetaRef.current.set(pm.id, pm);
                    const pets = m.pets.map(d => mergePet(d, petMetaRef.current.get(d.id)));
                    // (#bw) static fields arrive only when they change — apply each only if present, else keep the last.
                    scene.applyWorld(pets, m.time); setTime(m.time);
                    scene.setTransit(m.transit, m.transitRoute);   // transit every tick; route only when it changes
                    if (m.lots) scene.setWorld(m.lots);
                    if (m.cliques) { scene.setCliques(m.cliques); setCliques(m.cliques); }
                    if (m.parties) scene.setParties(m.parties);
                    if (m.city) { scene.setCity(m.city); setCityProject(m.city.project ?? null); }   // (#civic)
                    if (m.market) scene.setMarket(m.market);
                    if (m.beacons) scene.setBeacons(m.beacons);
                    if (m.concert !== undefined) scene.setConcert(m.concert);
                    if (m.games) scene.setGames(m.games);   // (#c4) live Connect 4 games
                    if (m.storylines) setStorylines(m.storylines);
                    if (m.leaderboards) setBoards(m.leaderboards);
                    if (embed) {   // (#embed) resolve the profile owner's pet (by id, or by username fallback), then pin the camera
                        let pid = embedPetIdRef.current;
                        if (!pid && embedCfg.profile) { const fp = pets.find(p => p.name.toLowerCase() === embedCfg.profile.toLowerCase()); if (fp) { pid = fp.id; embedPetIdRef.current = pid; setEmbedPetId(pid); } }
                        if (pid) {
                            if (!embedGpsSent.current) { embedGpsSent.current = true; ws!.send({ t: 'gps', petId: pid }); }   // center the gateway's frame on this pet so it stays in-frame on any profile
                            const fp = pets.find(p => p.id === pid); if (fp) setEmbedPetName(fp.name);   // camera follow is applied by the effect on (pet/view/lock), not per-frame
                        }
                    }
                    // (#name) track whether the viewer's own pet is named; gently prompt once if not
                    if (myPetIdRef.current) {
                        const mine = pets.find(p => p.id === myPetIdRef.current);
                        if (mine) {
                            setMyPetNamed(mine.named);
                            // (#profilepets) the OWNER in their profile's Style Editor with the block on but no
                            // named pet → this IS the initialization moment: prompt for the pet name right away
                            const ownEdit = embed && embedCfg.edit && embedCfg.pet === myPetIdRef.current;
                            const guard = ownEdit ? 'ppet_edit_prompted' : 'ppet_named_prompted';
                            if ((!embed || ownEdit) && !mine.named && !autoPrompted.current && !localStorage.getItem(guard)) {
                                autoPrompted.current = true; try { localStorage.setItem(guard, '1'); } catch { /* private mode */ }
                                setNameInput(''); setNameConfirm(''); setNameError(''); setNameOpen(true);
                            }
                        }
                    }
                }
                else if (m.t === 'petinfo') { if (!embed) { setFocus({ pet: m.pet, genome: m.genome, address: m.address }); scene.setFocus(m.pet.id); } }   // embed controls its own camera
                else if (m.t === 'events') {
                    setFeed((f) => [...m.events.filter(e => e.notability >= 2).reverse(), ...f].slice(0, 30));
                    const heads = m.events.filter(e => e.notability >= 3).map(e => e.summary);
                    if (heads.length) setTicker((t) => [...heads, ...t].slice(0, 16));
                    // #20 — your-pet-did-something notifications
                    const myId = myPetIdRef.current, myName = myNameRef.current;
                    if (myId) {
                        const mine = m.events.filter(e => e.notability >= 2 && (e.petId === myId || (myName && e.summary.includes(myName))));
                        if (mine.length) {
                            setNotifs((n) => [...mine.reverse(), ...n].slice(0, 30));
                            setUnread((u) => u + mine.length);
                            const top = mine[0]!;
                            setPopNote(top.summary); setTimeout(() => setPopNote(null), 6000);
                            if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden)
                                try { new Notification('🐾 Your pet did something!', { body: top.summary, tag: 'ppet' }); } catch { /* ignore */ }
                        }
                    }
                }
                else if (m.t === 'diary') { setDiary(m.lines); setDiaryLoading(false); setDiaryDay(m.day); }
                else if (m.t === 'mail') { setMailUnread(m.unread); }
                else if (m.t === 'letters') { setLetters(m.letters); }
                else if (m.t === 'named') {
                    setNameBusy(false);
                    if (m.ok) { setNameOpen(false); setNameError(''); setMyPetNamed(true); setNamedDone(m.name ?? 'Your pet'); }   // → "show on your profile?" step
                    else setNameError(m.error ?? 'Could not name your pet.');
                }
                else if (m.t === 'forecast') { setForecast({ now: m.now, hours: m.hours }); }
                else if (m.t === 'lotinfo') { setLot(m.info); }
                else if (m.t === 'err') { setToast(m.code === 'not_found' ? 'No pet by that name (yet).' : 'Something hiccuped.'); setTimeout(() => setToast(null), 2500); }
            };
        })();
        return () => { cancelled = true; ws?.close(); };
    }, []);

    // (#8/#sound) unlock audio on the first user gesture (browser autoplay policy)
    useEffect(() => {
        const unlock = () => { concertAudio.unlock(); ambient.userGesture(); };
        window.addEventListener('pointerdown', unlock, { once: true });
        window.addEventListener('keydown', unlock, { once: true });
        return () => { window.removeEventListener('pointerdown', unlock); window.removeEventListener('keydown', unlock); };
    }, []);

    useEffect(() => { const h = () => setIsFull(!!document.fullscreenElement); document.addEventListener('fullscreenchange', h); return () => document.removeEventListener('fullscreenchange', h); }, []);
    // (#embed) apply the camera target + lock ONLY when the pet / view / lock changes — never per frame
    // (re-applying every world frame would re-pin focus and yank a free-pan view back). Locked = pinned;
    // unlocked = centre once on the target, then you're free to drag away.
    useEffect(() => {
        if (!embed) return; const sc = sceneRef.current; if (!sc) return;
        embedViewRef.current = embedView;
        sc.setEmbed(embedLocked);
        if (embedPetId) sc.setFollow(embedPetId, embedView);
    }, [embedLocked, embedView, embedPetId, embed, sceneReady]);
    // (#embed) sensible default per mode: pinned in the compact widget, free to roam in fullscreen (toggle overrides)
    useEffect(() => { if (embed) setEmbedLocked(!isFull); }, [isFull, embed]);
    const toggleFull = () => {
        const el = containerRef.current ?? document.documentElement; if (!el) return;
        if (document.fullscreenElement) { document.exitFullscreen?.().catch(() => {}); return; }
        el.requestFullscreen?.().catch(() => { if (embed) window.open(`${location.pathname}?embed=1&profile=${encodeURIComponent(embedPetName)}&view=${embedView}`, '_blank'); });
    };

    const openNameModal = () => { setNameInput(''); setNameConfirm(''); setNameError(''); setNameOpen(true); };
    const dismissAdopt = () => { setAdoptDismissed(true); try { localStorage.setItem('ppet_adopt_dismissed', '1'); } catch { /* private mode */ } };
    const answerShowOnProfile = (on: boolean) => { wsRef.current?.send({ t: 'profileWidget', on }); setNamedDone(null); setToast(on ? '🐾 Your pet now lives on your profile!' : '🏷️ Named! (Your profile stays pet-free — change it any time in profile edit.)'); setTimeout(() => setToast(null), 4200); };
    const submitName = (e: FormEvent) => {
        e.preventDefault();
        const n = nameInput.trim();
        if (n.length < 2) { setNameError('Please enter at least 2 characters.'); return; }
        if (n.length > 20) { setNameError('Keep it to 20 characters or fewer.'); return; }
        if (n !== nameConfirm.trim()) { setNameError("The two names don't match — please retype to confirm."); return; }
        setNameBusy(true); setNameError(''); wsRef.current?.send({ t: 'name', name: n });
    };

    const gpsMe = () => { if (myPetId) { wsRef.current?.send({ t: 'gps', petId: myPetId }); sceneRef.current?.setFocus(myPetId); } };
    const toggleCamLock = () => { const next = !camLocked; setCamLocked(next); sceneRef.current?.setCameraLock(next, myPetId); };
    // (#sound) one toggle for ALL audio (ambient world + concerts). The click itself is the
    // browser-autoplay unlock gesture, so turning it on can start sound immediately.
    const toggleAudio = () => {
        const next = !audioOn; setAudioOn(next);
        try { localStorage.setItem('ppets_audio', next ? '1' : '0'); } catch { /* private mode */ }
        concertAudio.unlock(); ambient.setEnabled(next);
        sceneRef.current?.setAudioEnabled(next);
    };
    const toggleNotifs = () => {
        setNotifOpen((o) => !o); setUnread(0);
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission().catch(() => {});
    };
    const doSearch = (e: FormEvent) => { e.preventDefault(); if (search.trim()) wsRef.current?.send({ t: 'follow', name: search.trim() }); };
    const openDiary = () => { if (focus) { setDiaryDay(todayISO()); wsRef.current?.send({ t: 'diary', petId: focus.pet.id }); setDiary([]); setDiaryLoading(true); } };
    const fetchDiaryDay = (day: string) => { if (focus) { setDiaryDay(day); wsRef.current?.send({ t: 'diary', petId: focus.pet.id, day }); setDiary([]); setDiaryLoading(true); } };
    const closeDiary = () => { setDiary(null); setDiaryLoading(false); };
    const openWx = () => { wsRef.current?.send({ t: 'forecast' }); setWxOpen(true); };
    const openMail = () => { wsRef.current?.send({ t: 'letters' }); setLetters([]); setMailUnread(0); };   // (#7) fetch + clear unread

    const g = focus?.genome;

    // (#embed) compact profile widget: just the canvas + a slim options header, camera pinned to the pet
    if (embed) {
        return (
            <div className={`root embed${isFull ? ' embed-full' : ''}`} ref={containerRef}>
                <canvas ref={canvasRef} className="stage" />
                <div className="embed-bar">
                    <span className="embed-title" title={embedPetName ? `${embedPetName}'s Profile Pet` : 'Profile Pet'}>🐾 {embedPetName || 'Pet'}</span>
                    <span className="embed-clock">{phaseIcon(time.phase)} {fmtHour(time.hour)}</span>
                    {time.festival && <span className="embed-fest" title={`${time.festival.name} — ${time.festival.desc}`}>{time.festival.emoji}</span>}
                    <span className="embed-spacer" />
                    <button className={`embed-btn${embedView === 'pet' ? ' on' : ''}`} title="Follow the pet around town" onClick={() => setEmbedView('pet')}>⟳</button>
                    <button className={`embed-btn${embedView === 'home' ? ' on' : ''}`} title="Watch the home area" onClick={() => setEmbedView('home')}>🏠</button>
                    <button className={`embed-btn${embedLocked ? ' on' : ''}`} title={embedLocked ? 'Camera locked to the pet — click to free it' : 'Free camera — click to lock to the pet'} onClick={() => setEmbedLocked(l => !l)}>{embedLocked ? '🔒' : '🔓'}</button>
                    <button className="embed-btn" title="Zoom out" onClick={() => sceneRef.current?.zoomBy(0.84)}>−</button>
                    <button className="embed-btn" title="Zoom in" onClick={() => sceneRef.current?.zoomBy(1.19)}>＋</button>
                    <button className={`embed-btn${audioOn ? ' on' : ''}`} title={audioOn ? 'Sound on — click to mute' : 'Sound is muted — click to hear the world'} onClick={toggleAudio}>{audioOn ? '🔊' : '🔇'}</button>
                    <button className="embed-btn" title="Fullscreen" onClick={toggleFull}>⛶</button>
                    {embedPetId > 0 && <a className="embed-btn" title="This pet's full profile" href={`/profilepets/pet/${embedPetId}`} target="_blank" rel="noopener noreferrer">📄</a>}
                    <a className="embed-btn" title="Open the whole city" href={location.pathname} target="_blank" rel="noopener noreferrer">🌆</a>
                </div>
                {conn !== 'open' && <div className="embed-status">{conn === 'connecting' ? 'connecting…' : conn === 'closed' ? 'reconnecting…' : conn}</div>}
                {conn === 'open' && !embedPetId && <div className="embed-status">looking for {embedPetName || 'the pet'}…</div>}
                {/* (#profilepets) the adopt infographic: members without a named pet get the adopt pitch;
                    guests get the same pitch + a free-account link. ✕ = just watch (remembered). */}
                {me && !adoptDismissed && !nameOpen && !namedDone && (me.id === 0 || !myPetNamed) && (
                    <div className="adopt-card">
                        <button className="adopt-x" title="No thanks — just watching" onClick={dismissAdopt}>✕</button>
                        <div className="adopt-art">🐾</div>
                        <div className="adopt-body">
                            <div className="adopt-h">Adopt a Profile Pet!</div>
                            {me.id > 0 ? (
                                <>
                                    <div className="adopt-p">A tiny companion that lives its own life in the pet city — naps, friends, drama and all — right here on your FFR profile.</div>
                                    <button className="btn primary adopt-cta" onClick={openNameModal}>🐾 Adopt &amp; name yours</button>
                                </>
                            ) : (
                                <>
                                    <div className="adopt-p">Pets live their own little lives in the FFR pet city — and one can live on your profile. You just need a <b>free FFR account</b>.</div>
                                    <a className="btn primary adopt-cta" href="https://www.flashflashrevolution.com/vbz/register.php" target="_blank" rel="noopener noreferrer">Create a free account</a>
                                </>
                            )}
                        </div>
                    </div>
                )}
                {nameOpen && <NameModal {...{ nameInput, setNameInput, nameConfirm, setNameConfirm, nameError, setNameError, nameBusy, submitName, setNameOpen }} />}
                {namedDone && <ShowOnProfileModal name={namedDone} answer={answerShowOnProfile} />}
                {toast && <div className="toast embed-toast">{toast}</div>}
            </div>
        );
    }

    return (
        <div className="root" ref={containerRef}>
            <canvas ref={canvasRef} className="stage" />

            <header className="topbar">
                <span className="brand">Profile Pet City</span>
                {backend === 'webgpu' && <span className="gfxbadge" title="WebGPU renderer active">⚡ GPU</span>}
                <span className={`phase phase-${time.phase}`}>{phaseIcon(time.phase)} {fmtHour(time.hour)}</span>
                <button className="phase wx-chip" title="Click for the forecast (Dunlap, IL)" onClick={openWx}>{weatherIcon(time.weather, time.phase)} {time.tempF}°F</button>
                {time.festival ? (
                    <span className="festival-wrap" style={{ position: 'relative', display: 'inline-flex' }}>
                        <button className="festival" title={time.festival.desc} onClick={() => setFestOpen(o => !o)} style={{ cursor: 'pointer' }}>{time.festival.emoji} {time.festival.name} <span style={{ opacity: 0.6 }}>ⓘ</span></button>
                        {festOpen && (
                            <div className="festival-pop" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 60, width: 250, background: 'rgba(18,16,30,0.97)', color: '#e7e0d4', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 8, padding: '10px 12px', fontSize: 12, lineHeight: 1.45, boxShadow: '0 8px 24px rgba(0,0,0,0.45)' }} onClick={() => setFestOpen(false)}>
                                <div style={{ fontWeight: 700, marginBottom: 4 }}>{time.festival.emoji} {time.festival.name}</div>
                                {time.festival.desc}
                                <div style={{ marginTop: 6, opacity: 0.65, fontSize: 11 }}>Decor is up all day; the gathering peaks in the evening.</div>
                            </div>
                        )}
                    </span>
                ) : null}
                <span className={`conn ${conn}`}>{conn}</span>
                <div className="spacer" />
                {hasPet && !myPetNamed && <button className="btn" onClick={openNameModal} title="Give your pet a name — one time only">🏷️ Name My Pet</button>}
                {hasPet && <button className={`btn${camLocked ? ' primary' : ''}`} onClick={toggleCamLock} title={camLocked ? 'Camera is locked to your pet — click to free it' : 'Lock the camera to follow your pet'}>{camLocked ? '🔒 Cam locked' : '🎥 Lock cam'}</button>}
                <button className={`btn${audioOn ? ' primary' : ''}`} onClick={toggleAudio} title={audioOn ? 'Sound on: ambient world + concerts — click to mute' : 'Sound is muted — click for rain, crickets, birdsong, trains & concerts'}>{audioOn ? '🔊' : '🔇'}</button>
                <button className="btn" onClick={toggleFull} title={isFull ? 'Exit fullscreen' : 'Fullscreen'}>{isFull ? '🗗' : '⛶'}</button>
                {hasPet ? <button className="btn primary" onClick={gpsMe}>📍 My pet</button>
                    : <span className="hint">{me && me.id > 0 ? '' : 'Log in to FFR to get your pet'}</span>}
                {hasPet && <button className="btn bell" onClick={toggleNotifs} title="Your pet's notifications">🔔{unread > 0 && <span className="badge">{unread > 9 ? '9+' : unread}</span>}</button>}
                {hasPet && <button className="btn mail" onClick={openMail} title="Pen-pal letters from your pet">{mailUnread > 0 ? '📬' : '📭'}{mailUnread > 0 && <span className="badge dot" />}</button>}
                <form onSubmit={doSearch} className="search">
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="find a pet by name…" />
                    <button className="btn">Find</button>
                </form>
            </header>

            {focus && (
                <aside className="panel">
                    <div className="pname">{focus.pet.named ? <span className="pname-plain">{focus.pet.name}</span> : <ProfileLink name={focus.pet.name} />}</div>
                    <div className="paddr">🏠 {focus.address}</div>
                    {g && <div className="parch">{g.species} · <b>{g.archetype}</b></div>}
                    <div className="pact">{focus.pet.emoji} {cap(activityPhrase(focus.pet))}</div>
                    {focus.pet.atHome ? <div className="pwhere">🏠 at home</div> : null}
                    {focus.pet.thought ? <div className="pthought">💭 {focus.pet.thought}</div> : null}
                    <div className="pmood">mood {affectFace(focus.pet.affect)}</div>
                    {focus.pet.cliqueId ? <div className="pclique">🫂 {cliques.find(c => c.id === focus.pet.cliqueId)?.name ?? 'a friend circle'}</div> : null}
                    {focus.pet.vet > 0 ? <div className="pvet">{vetIcon(focus.pet.vet)} FFR {vetLabel(focus.pet.vet)}{focus.pet.since ? ` · since ${focus.pet.since}` : ''}</div> : null}
                    {focus.pet.rank > 0 ? <div className="prank">⚔️ FFR rank #{focus.pet.rank.toLocaleString()} · {rankLabel(focus.pet.rankTier)}</div> : null}
                    {focus.pet.flair ? <div className={`pflair${focus.pet.legend ? ' legend' : ''}`}>{focus.pet.flair}</div> : null}
                    {g?.quirks?.length ? <div className="pquirks">“{g.quirks.join('”, “')}”</div> : null}
                    {focus.pet.stash?.length ? <div className="pstash"><span className="pstash-h">📦 brought home</span>{focus.pet.stash.map(s => <span key={s.name} className="pstash-item">{s.emoji} {s.count}× {s.name}</span>)}</div> : null}
                    <button className="btn wide" onClick={openDiary}>📖 Today's diary</button>
                    <a className="btn wide" href={`/profilepets/pet/${focus.pet.id}`} target="_blank" rel="noopener noreferrer" style={{ textAlign: 'center', textDecoration: 'none' }}>📄 Full profile ↗</a>
                </aside>
            )}

            <section className="feed">
                <div className="feed-h"><span>As the City Turns</span>
                    <span className="feed-actions">
                        {boards.length > 0 && <button className="tales-btn" onClick={() => setBoardsOpen(true)}>🏆 Boards</button>}
                        {storylines.length > 0 && <button className="tales-btn" onClick={() => setTalesOpen(true)}>📖 Tales ({storylines.length})</button>}
                    </span>
                </div>
                {feed.length === 0 ? <div className="feed-empty">…the city is waking up…</div>
                    : feed.map((e, i) => <div key={i} className="feed-line">{e.summary}</div>)}
            </section>

            {diary && (
                <div className="modal-bg" onClick={closeDiary}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-h">📖 {focus?.pet.name}'s Diary</div>
                        <div className="diary-nav">
                            <button className="diary-arrow" disabled={diaryLoading || daysAgo(diaryDay) >= 14} title="Earlier day" onClick={() => fetchDiaryDay(addDays(diaryDay, -1))}>‹</button>
                            <span className="diary-day">{prettyDay(diaryDay)}</span>
                            <button className="diary-arrow" disabled={diaryLoading || daysAgo(diaryDay) <= 0} title="Later day" onClick={() => fetchDiaryDay(addDays(diaryDay, 1))}>›</button>
                        </div>
                        {diaryLoading
                            ? <div className="diary-loading"><div className="book">📖</div><div className="diary-loading-txt">turning the pages…</div></div>
                            : diary.map((l, i) => <p key={i} className="diary-line">{l}</p>)}
                        <button className="btn" onClick={closeDiary}>close</button>
                    </div>
                </div>
            )}

            {letters && (
                <div className="modal-bg" onClick={() => setLetters(null)}>
                    <div className="modal letters-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-h">📬 Letters from your pet</div>
                        {letters.length === 0
                            ? <div className="diary-loading"><div className="book">📭</div><div className="diary-loading-txt">no letters yet — your pet will write soon</div></div>
                            : letters.map((l, i) => (
                                <div key={i} className="letter">
                                    <div className="letter-date">{prettyDay(new Date(l.ts * 1000).toISOString().slice(0, 10))}</div>
                                    {l.lines.map((ln, j) => <p key={j} className="letter-line">{ln}</p>)}
                                </div>
                            ))}
                        <button className="btn" onClick={() => setLetters(null)}>close</button>
                    </div>
                </div>
            )}

            {wxOpen && (
                <div className="modal-bg" onClick={() => setWxOpen(false)}>
                    <div className="modal wx-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-h">{weatherIcon(time.weather, time.phase)} Weather — Profile Pet City</div>
                        <div className="wx-now">
                            <div className="wx-now-temp">{time.tempF}°F</div>
                            <div className="wx-now-desc">
                                <div className="wx-now-main">{cap(time.weatherDesc)}</div>
                                <div className="wx-now-sub">
                                    {forecast ? `feels ${forecast.now.feelsF}°F · ` : ''}{time.season} · wind {time.windMph}{forecast && forecast.now.gustMph > forecast.now.windMph ? `–${forecast.now.gustMph}` : ''} mph{forecast ? ` · humidity ${forecast.now.humidity}%` : ''}
                                </div>
                            </div>
                        </div>
                        <div className="wx-sec">Next 12 hours</div>
                        {!forecast
                            ? <div className="wx-loading">📡 fetching the forecast…</div>
                            : <div className="wx-hours">
                                {forecast.hours.map((h, i) => (
                                    <div key={i} className="wx-hour">
                                        <span className="wx-h-time">{h.label}</span>
                                        <span className="wx-h-emoji">{h.emoji}</span>
                                        <span className="wx-h-temp">{h.tempF}°</span>
                                        <span className="wx-h-pop">{h.pop >= 10 ? `💧${h.pop}%` : ''}</span>
                                    </div>
                                ))}
                            </div>}
                        <div className="wx-foot">Live from FFR Profile Pet City · updates every 15 min</div>
                        <button className="btn" onClick={() => setWxOpen(false)}>close</button>
                    </div>
                </div>
            )}

            {lot && (
                <div className="modal-bg" onClick={() => setLot(null)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-h">{propIcon(lot.lotType)} {propTitle(lot)}</div>
                        <div className="prop-addr">📍 {lot.address}</div>
                        {lot.resident && <div className="prop-row"><b>Resident:</b> {lot.resident.named ? <span className="pname-plain">{lot.resident.name}</span> : <ProfileLink name={lot.resident.name} />} · {lot.resident.species} ({lot.resident.archetype})</div>}
                        <div className="prop-sec">Here right now</div>
                        {lot.occupants.length === 0 ? <div className="prop-empty">— nobody home —</div>
                            : lot.occupants.map(o => <div key={o.id} className="prop-occ">{o.emoji} {o.named ? <span className="pname-plain occ">{o.name}</span> : <ProfileLink name={o.name} className="occ" />} — {o.verb}</div>)}
                        <div className="prop-sec">{lot.lotType === 'home' ? 'Belongings' : 'What\'s here'}</div>
                        <div className="prop-items">{lot.items.map((it, i) => <span key={i} className="prop-item">{it.emoji} {it.name}</span>)}</div>
                        <div className="prop-sec">Recent goings-on</div>
                        {lot.events.length === 0 ? <div className="prop-empty">— quiet lately —</div>
                            : lot.events.map((e, i) => <div key={i} className="prop-evt">{e.summary} <span className="prop-ago">{ago(e.ts)}</span></div>)}
                        <button className="btn" onClick={() => setLot(null)}>close</button>
                    </div>
                </div>
            )}

            {talesOpen && (
                <div className="modal-bg" onClick={() => setTalesOpen(false)}>
                    <div className="modal tales" onClick={e => e.stopPropagation()}>
                        <div className="modal-h">📖 Tales of the City</div>
                        {storylines.length === 0 ? <p className="diary-line">…no tales are unfolding just yet.</p>
                            : storylines.map(s => (
                                <div key={s.id} className="tale">
                                    <div className="tale-h">
                                        <span className="tale-title">{s.title}</span>
                                        <span className={`tale-status tale-${s.kind}`}>{s.status}</span>
                                    </div>
                                    {s.chapters.map((c, i) => <div key={i} className="tale-ch">— {c}</div>)}
                                    <div className="tale-jump">
                                        {s.parts.map(p => <button key={p.id} className="linklike" onClick={() => { wsRef.current?.send({ t: 'gps', petId: p.id }); sceneRef.current?.setFocus(p.id); setTalesOpen(false); }}>📍 {p.name}</button>)}
                                    </div>
                                </div>
                            ))}
                        <button className="btn" onClick={() => setTalesOpen(false)}>close</button>
                    </div>
                </div>
            )}

            {boardsOpen && (
                <div className="modal-bg" onClick={() => setBoardsOpen(false)}>
                    <div className="modal tales" onClick={e => e.stopPropagation()}>
                        <div className="modal-h">🏆 Cozy Leaderboards</div>
                        {cityProject && (
                            <div className="board" style={{ marginBottom: 8 }}>
                                <div className="board-h">🏗️ Civic Fund — {cityProject.emoji} {cityProject.name}</div>
                                <div className="board-row" style={{ cursor: 'default' }}>
                                    {cityProject.phase === 'funding'
                                        ? <span>raising <b>{cityProject.fund}/{cityProject.goal}¢</b> · {cityProject.donors} donor{cityProject.donors === 1 ? '' : 's'}</span>
                                        : <span>under construction — <b>{cityProject.pct}%</b> built</span>}
                                </div>
                            </div>
                        )}
                        <div className="boards-grid">
                            {boards.map(b => (
                                <div key={b.title} className="board">
                                    <div className="board-h">{b.emoji} {b.title}</div>
                                    {b.entries.map((e, i) => (
                                        <button key={e.petId} className="board-row linklike" onClick={() => { wsRef.current?.send({ t: 'gps', petId: e.petId }); sceneRef.current?.setFocus(e.petId); setBoardsOpen(false); }}>
                                            <span className="board-rank">{['🥇', '🥈', '🥉', '4.'][i]}</span> <b>{e.name}</b> <span className="board-val">{e.value}</span>
                                        </button>
                                    ))}
                                </div>
                            ))}
                        </div>
                        <button className="btn" onClick={() => setBoardsOpen(false)}>close</button>
                    </div>
                </div>
            )}

            {ticker.length > 0 && (
                <div className="ticker"><div className="ticker-tape">
                    {ticker.concat(ticker).map((h, i) => <span key={i} className="ticker-item">📣 {h}</span>)}
                </div></div>
            )}

            {notifOpen && (
                <aside className="notif-panel" onClick={e => e.stopPropagation()}>
                    <div className="notif-h">🔔 Your pet, {me?.name}
                        <button className="notif-x" onClick={() => setNotifOpen(false)}>✕</button>
                    </div>
                    {typeof Notification !== 'undefined' && Notification.permission !== 'granted' &&
                        <div className="notif-perm">Enable browser alerts to hear when your pet gets up to something while you're away.</div>}
                    {notifs.length === 0 ? <div className="notif-empty">No moments yet — your pet's up to ordinary things. Check back!</div>
                        : notifs.map((e, i) => <div key={i} className="notif-item" onClick={() => { gpsMe(); setNotifOpen(false); }}>{e.summary}</div>)}
                </aside>
            )}

            {popNote && <div className="popnote" onClick={gpsMe}>🐾 {popNote}</div>}

            {nameOpen && <NameModal {...{ nameInput, setNameInput, nameConfirm, setNameConfirm, nameError, setNameError, nameBusy, submitName, setNameOpen }} />}
            {namedDone && <ShowOnProfileModal name={namedDone} answer={answerShowOnProfile} />}

            {toast && <div className="toast">{toast}</div>}
        </div>
    );
}

function fmtHour(h: number): string { const hh = Math.floor(h); const m = Math.floor((h - hh) * 60); return `${String(hh).padStart(2, '0')}:${String(m).padStart(2, '0')}`; }
function phaseIcon(p: string): string { return p === 'night' ? '🌙' : p === 'dawn' ? '🌅' : p === 'dusk' ? '🌆' : '☀️'; }
function weatherIcon(w: string, phase?: string): string {
    if (phase === 'night' && (w === 'clear' || w === 'heat' || w === 'petals' || w === 'leaves')) return '🌙';
    return ({ rain: '🌧️', snow: '❄️', petals: '🌸', leaves: '🍂', heat: '🔥', cloudy: '☁️', storm: '⛈️', fog: '🌫️', clear: '☀️' } as Record<string, string>)[w] ?? '☀️';
}
function vetIcon(v: number): string { return v >= 3 ? '🏆' : v === 2 ? '🎖️' : '✨'; }
function vetLabel(v: number): string { return v >= 3 ? 'O.G.' : v === 2 ? 'veteran' : 'longtime member'; }
function rankLabel(t: number): string { return ['unranked', 'Ranked', 'Skilled', 'Expert', 'Master', 'Legend'][t] ?? 'Ranked'; }
function propIcon(type: string): string { return ({ home: '🏠', grocery: '🛒', general: '🏬', park: '🌳', plaza: '☕', arcade: '🕹️', 'post-office': '📮', concert: '🎤', fastfood: '🍔', tacos: '🌮' } as Record<string, string>)[type] ?? '🏙️'; }
// (#10) a pet's name IS its owner's FFR username → link to their profile
// (#name) shared one-time naming + adopt modal (double-entry to confirm)
function NameModal(p: {
    nameInput: string; setNameInput: (s: string) => void; nameConfirm: string; setNameConfirm: (s: string) => void;
    nameError: string; setNameError: (s: string) => void; nameBusy: boolean; submitName: (e: FormEvent) => void; setNameOpen: (b: boolean) => void;
}) {
    const a = p.nameInput.trim(), b = p.nameConfirm.trim();
    return (
        <div className="modal-bg" onClick={() => !p.nameBusy && p.setNameOpen(false)}>
            <form className="modal name-modal" onClick={e => e.stopPropagation()} onSubmit={p.submitName}>
                <div className="modal-h">🏷️ Adopt &amp; Name Your Pet</div>
                <p className="name-note">Choose your pet's name. <b>You can only do this once</b> — it can't be changed later, so pick carefully. This sets up your Profile Pet on your own profile and shows this name instead of your FFR username, keeping you private.</p>
                <input className="name-input" autoFocus maxLength={20} value={p.nameInput} onChange={e => { p.setNameInput(e.target.value); p.setNameError(''); }} placeholder="e.g. Mochi, Sir Wiggles…" />
                <input className="name-input" maxLength={20} value={p.nameConfirm} onChange={e => { p.setNameConfirm(e.target.value); p.setNameError(''); }} placeholder="Type the name again to confirm" />
                {b.length > 0 && a !== b && !p.nameError && <div className="name-hint">The names don't match yet…</div>}
                {p.nameError && <div className="name-error">⚠️ {p.nameError}</div>}
                <div className="name-actions">
                    <button type="button" className="btn" onClick={() => p.setNameOpen(false)} disabled={p.nameBusy}>Maybe later</button>
                    <button type="submit" className="btn primary" disabled={p.nameBusy || a.length < 2 || a !== b}>{p.nameBusy ? 'Naming…' : 'Adopt my pet'}</button>
                </div>
            </form>
        </div>
    );
}
// (#profilepets) post-adoption step: their pet ALREADY shows on their profile (set on naming);
// this asks whether to keep it there. Either way it's editable later in profile edit.
function ShowOnProfileModal(p: { name: string; answer: (on: boolean) => void }) {
    return (
        <div className="modal-bg" onClick={() => p.answer(true)}>
            <div className="modal name-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-h">🎉 {p.name} is yours!</div>
                <p className="name-note">Your new pet just moved into the city — and onto <b>your FFR profile</b>, where visitors can watch it live. Want to keep it there? (You can change this any time under profile edit.)</p>
                <div className="name-actions">
                    <button type="button" className="btn" onClick={() => p.answer(false)}>Hide from my profile</button>
                    <button type="button" className="btn primary" onClick={() => p.answer(true)}>Show it on my profile</button>
                </div>
            </div>
        </div>
    );
}
function profileHref(name: string): string { return `https://www.flashflashrevolution.com/profile/${encodeURIComponent(name)}`; }
function ProfileLink({ name, className }: { name: string; className?: string }) {
    return <a className={`plink${className ? ' ' + className : ''}`} href={profileHref(name)} target="_blank" rel="noopener noreferrer" title={`Open ${name}'s FFR profile`}>{name} <span className="plink-ext">↗</span></a>;
}
function propTitle(l: LotInfo): string {
    if (l.lotType === 'home') return l.resident ? `${l.resident.name}'s home` : 'A little home';
    return ({ grocery: 'The Grocery', general: 'General Store', park: 'The Park', plaza: 'The Plaza', arcade: 'The Arcade', 'post-office': 'The Post Office', concert: 'Concert Grounds', fastfood: 'AAA Burgers', tacos: 'Triple Salsa Taquería' } as Record<string, string>)[l.lotType] ?? 'A spot in town';
}
function ago(ts: number): string { const s = Math.max(0, Math.floor(Date.now() / 1000) - ts); if (s < 90) return 'just now'; const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`; const h = Math.floor(m / 60); return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`; }
// mirrors server/src/sim/affect.ts AFFECT codes
const AFFECTS: { emoji: string; label: string }[] = [
    { emoji: '🙂', label: 'neutral' }, { emoji: '😊', label: 'content' }, { emoji: '😌', label: 'calm' },
    { emoji: '😑', label: 'bored' }, { emoji: '😔', label: 'sad' }, { emoji: '😟', label: 'anxious' }, { emoji: '😄', label: 'excited' },
];
function affectFace(code: number): string { const a = AFFECTS[code] ?? AFFECTS[0]!; return `${a.emoji} ${a.label}`; }
function cap(s: string): string { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; }
// social verbs dangle on a preposition meant to take the partner's name (the wire verb omits it) —
// so "…with" alone reads as broken. Complete it with "a friend" when the pet has a partner.
function activityPhrase(p: WirePet): string {
    if (p.targetId > 0 && /\b(with|to|for|against|alongside|beside)$/.test(p.verb)) return `${p.verb} a friend`;
    return p.verb;
}
function todayISO(): string { return new Date().toISOString().slice(0, 10); }
function addDays(iso: string, n: number): string { const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }
function daysAgo(iso: string): number { if (!iso) return 0; return Math.round((Date.parse(`${todayISO()}T00:00:00Z`) - Date.parse(`${iso}T00:00:00Z`)) / 86400000); }
function prettyDay(iso: string): string {
    if (!iso) return '';
    const d = daysAgo(iso);
    if (d === 0) return 'Today'; if (d === 1) return 'Yesterday';
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}
